// ── Launch switch ────────────────────────────────────────────────────────────
// The app "goes live" automatically when REACT_APP_LAUNCH_DATE passes — no second
// redeploy needed once the launch date + real Turnstile key are baked into the build.
// REACT_APP_LAUNCHED=on|off force-overrides the schedule (launch early or hold).
// At launch: the site gate drops (full app opens), the FOMO counter auto-activates,
// AND Turnstile swaps from the Cloudflare test key to the configured production key.
export const LAUNCH_DATE =
  process.env.REACT_APP_LAUNCH_DATE || "2026-08-15T15:00:00Z";

const LAUNCH_OVERRIDE = (process.env.REACT_APP_LAUNCHED || "").toLowerCase();

export function isLaunched() {
  if (LAUNCH_OVERRIDE === "on") return true;
  if (LAUNCH_OVERRIDE === "off") return false;
  const t = new Date(LAUNCH_DATE).getTime();
  return Number.isFinite(t) && Date.now() >= t;
}

// Site gate. "/" serves the waitlist lander until the backend's Launch Control reports launched.
// Overrides: REACT_APP_GATE=off → always open now; =force → stay gated even post-launch.
const GATE_SETTING = (process.env.REACT_APP_GATE || "on").toLowerCase();
// Build-time fallback used only until GET /config/public resolves — fail CLOSED (gated) so the
// full landing never flashes before Launch Control has been consulted.
export const GATE_ENABLED = GATE_SETTING !== "off";

// Runtime gate resolver. Once public config has loaded, the backend's `launched` flag (admin
// Launch Control) is the source of truth — the env schedule/override only applies when the backend
// doesn't report it. `REACT_APP_GATE`: force → always gated · off → always open.
export function gateEnabled(publicCfg) {
  if (GATE_SETTING === "force") return true;
  if (GATE_SETTING === "off") return false;
  if (typeof publicCfg?.launched === "boolean") return !publicCfg.launched;
  return !isLaunched();
}

// Public share-link base (creator pack links). Call sites fall back to window.location.origin.
export const SHARE_BASE = process.env.REACT_APP_SHARE_BASE || "";

// Brand X/Twitter handle used in prefilled "Share to X" tweets (compose window — human confirms before posting).
export const X_HANDLE = "BLOQTalk";

// Which lander variant renders at "/" (1=SignalLock, 2=Console, 3=TrustGrid, 4=Vault).
export const DEFAULT_LANDER = Number(process.env.REACT_APP_LANDER || 1);

// Web3 micro-transactions (tips/gifts, boosts, sticker marketplace, token-gated BLOQs).
// OFF by default — the backend contracts are mobile-owned and PENDING (see
// /app/HANDOFF_microtx_and_block.md). Two ways to turn it on:
//  1. Build flag REACT_APP_MICROTX=on — global, for ALL users (use once endpoints ship).
//  2. Per-browser preview override (localStorage) — admin-only dogfooding on THIS device only.
//     Never affects real users or production; calls to unbuilt endpoints just show "coming soon".
export const MICROTX_PREVIEW_KEY = "bloq_preview_microtx";
const MICROTX_BUILD = (process.env.REACT_APP_MICROTX || "").toLowerCase() === "on";
export function isMicrotxPreviewOn() {
  try { return localStorage.getItem(MICROTX_PREVIEW_KEY) === "on"; } catch { return false; }
}
export function setMicrotxPreview(on) {
  try { on ? localStorage.setItem(MICROTX_PREVIEW_KEY, "on") : localStorage.removeItem(MICROTX_PREVIEW_KEY); } catch { /* no-op */ }
}
export const MICROTX_BUILD_ON = MICROTX_BUILD;
// Evaluated once at module load; toggling the preview requires a page reload to apply.
export const MICROTX_ENABLED = MICROTX_BUILD || isMicrotxPreviewOn();

// In-chat crypto sends (Lane A — send USDC/ETH to a DM peer, Base Sepolia testnet for now).
// OFF by default; backend endpoints (receiving-wallet / intent / confirm) are mobile-owned and
// PENDING (see /app/HANDOFF_differentiation_v1.md). Same two switches as MICROTX:
//  1. Build flag REACT_APP_CRYPTO=on — global.
//  2. Per-browser preview override (localStorage) — admin dogfooding on THIS device only.
// The client-side send flow works today on testnet; unbuilt backend calls degrade gracefully.
export const CRYPTO_PREVIEW_KEY = "bloq_preview_crypto";
const CRYPTO_BUILD = (process.env.REACT_APP_CRYPTO || "").toLowerCase() === "on";
export function isCryptoPreviewOn() {
  try { return localStorage.getItem(CRYPTO_PREVIEW_KEY) === "on"; } catch { return false; }
}
export function setCryptoPreview(on) {
  try { on ? localStorage.setItem(CRYPTO_PREVIEW_KEY, "on") : localStorage.removeItem(CRYPTO_PREVIEW_KEY); } catch { /* no-op */ }
}
export const CRYPTO_ENABLED = CRYPTO_BUILD || isCryptoPreviewOn();

// Cloudflare Turnstile "always-passes" test key — used only as a fallback when no
// real key is configured.
export const TURNSTILE_TEST_KEY = "1x00000000000000000000AA";

// Effective Turnstile site key (public, safe to embed). Uses the real configured key
// whenever REACT_APP_TURNSTILE_SITE_KEY is set (bot-protection is live on the pre-launch
// waitlist too), else falls back to the harmless test key.
export const TURNSTILE_SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY || TURNSTILE_TEST_KEY;

// FOMO waitlist counter. Frozen at FOMO_BASE until it's explicitly on OR the app has
// launched, then it climbs with random momentum. Decoupled from the backend count.
export const FOMO_ACTIVE =
  (process.env.REACT_APP_FOMO_ACTIVE || "off").toLowerCase() === "on" || isLaunched();
export const FOMO_BASE = Number(process.env.REACT_APP_FOMO_BASE || 2);

export const LANDER_VARIANTS = {
  1: "SignalLock",
  2: "Console",
  3: "TrustGrid",
  4: "Vault",
};

// ── Environment (runtime) ────────────────────────────────────────────────────
// A single build serves both preview and the live prod domain, so we detect the
// live host at runtime rather than at build time. Preview/dev stays fully open.
export function isProdHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "bloqtalk.app" || h === "www.bloqtalk.app";
}

// ── Admin console beta hardening ─────────────────────────────────────────────
// On the public prod domain the admin console is hidden to shrink the attack surface
// during beta. Founders unlock it once per browser via  /admin/login?key=<CODE>
// (code baked from REACT_APP_ADMIN_ACCESS_CODE). Preview/dev is always unlocked, and
// if no code is configured we never lock anyone out.
export const ADMIN_ACCESS_CODE = process.env.REACT_APP_ADMIN_ACCESS_CODE || "";
const ADMIN_GATE_STORE = "bloq_admin_gate";

export function adminConsoleUnlocked() {
  if (!isProdHost()) return true;
  if (!ADMIN_ACCESS_CODE) return true;
  try {
    const url = new URL(window.location.href);
    const key = url.searchParams.get("key");
    if (key && key === ADMIN_ACCESS_CODE) {
      localStorage.setItem(ADMIN_GATE_STORE, ADMIN_ACCESS_CODE);
      url.searchParams.delete("key");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
      return true;
    }
    return localStorage.getItem(ADMIN_GATE_STORE) === ADMIN_ACCESS_CODE;
  } catch {
    return false;
  }
}
